import { useTransactionById } from '@/hooks/transaction/use-transaction-by-id';
import { toPendingTransactionSignables } from '@/components/transactions/batch-transaction-utils';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import axios from 'axios';
import { AlertCircle, Settings } from 'lucide-react';
import {
  AccountTransactionDecision,
  type AccountTransactionDraft,
} from '@mls-wallet/shared-types';
import { TransactionBuilderHeader } from '../../components/transaction-builder/TransactionBuilderHeader';
import { TransactionBuilderFooter } from '../../components/transaction-builder/TransactionBuilderFooter';
import { useTransactionBuilderStore } from '../../components/transaction-builder/useTransactionBuilderStore';
import { buildBatchAccountTransactionDraft } from '../../components/transaction-builder/batch-transaction-draft';
import { BatchTransactionReview } from '../../components/transaction-builder/BatchTransactionReview';
import { useBatchSimulation } from '../../components/transaction-builder/useBatchSimulation';
import { TransactionSimulationSheet } from '../../components/transaction-builder/transaction-simulation/TransactionSimulationSheet';
import { TransactionSubmitSuccessDialog } from '../../components/transaction-builder/TransactionSubmitSuccessDialog';
import { useCreateTransaction } from '@/hooks/transaction/use-create-transaction';
import { usePendingSignaturesState } from '@/hooks/admin/member/use-pending-signatures-state';
import { PendingSignaturesModal } from '@/components/modals/pending-signatures-modal';
import { useCreateTransactionSignature } from '@/hooks/transaction/use-create-transaction-signature';
import { SuccessDialogState } from '@/components/transaction-builder/enums';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuthClient } from '@/lib/authClient';

type DenApiErrorResponse = {
  error?: {
    body?: {
      cause?: unknown;
    };
  };
};

type TransactionSubmissionError = {
  stage: 'creation' | 'signing';
  message: string;
};

/** Adapts an unknown Den API envelope to its trimmed, non-empty cause. */
function axiosErrorCause(error: unknown): string | undefined {
  if (!axios.isAxiosError<DenApiErrorResponse>(error)) return undefined;

  const body = error.response?.data?.error?.body;
  if (!body || !Object.prototype.hasOwnProperty.call(body, 'cause')) {
    return undefined;
  }

  const cause = body.cause;
  return typeof cause === 'string' && cause.trim().length > 0
    ? cause.trim()
    : undefined;
}

/** Adapts a submission stage and unknown failure into a domain error. */
function submissionError(
  stage: TransactionSubmissionError['stage'],
  error: unknown
): TransactionSubmissionError {
  const cause = axiosErrorCause(error);
  return {
    stage,
    message:
      cause ??
      (stage === 'signing'
        ? 'The transaction was created, but its approval signature could not be started. Please try signing again.'
        : 'An unexpected error occurred while creating the transaction. Please try again.'),
  };
}

/** Review the complete cart and request a separate initiator signature for every child. */
export function TransactionBuilderReviewPage(): React.ReactElement {
  const account = useTransactionBuilderStore((state) => state.account);
  const network = useTransactionBuilderStore((state) => state.network);
  const children = useTransactionBuilderStore(
    (state) => state.batchTransactions
  );
  const description = useTransactionBuilderStore((state) => state.description);
  const reset = useTransactionBuilderStore((state) => state.reset);
  const { user } = useAuthClient();
  const { request, revision } = useBatchSimulation();
  const [simulationOpen, setSimulationOpen] = useState(false);
  // The state we need to manage the pending signatures modal (to sign approval for new tx)
  const {
    pendingSignableIds,
    setPendingSignableIds,
    pendingDecision,
    setPendingDecision,
    isPendingModalOpen,
    setIsPendingModalOpen,
    resetPendingState,
  } = usePendingSignaturesState();
  const { createTransaction, clearError } = useCreateTransaction();
  const { createSignature } = useCreateTransactionSignature();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [successDialogState, setSuccessDialogState] =
    useState<SuccessDialogState>(SuccessDialogState.Closed);
  // Once the transaction is created, but before it's signed, store its ID to use later
  const [createdTransactionId, setCreatedTransactionId] = useState<
    string | null
  >(null);
  const [transactionSubmissionError, setTransactionSubmissionError] =
    useState<TransactionSubmissionError | null>(null);

  const {
    transaction: signingTransaction,
    refetch: refetchSigningTransaction,
  } = useTransactionById(createdTransactionId, isPendingModalOpen ? 1000 : 0);

  const handleApproveAndSign = async (transactionId: string) => {
    // Create an approval signature for the transaction
    const signatures = await createSignature({
      transactionId: transactionId,
      decision: AccountTransactionDecision.INITIATE,
    });
    // Refresh the created parent even while its unsigned children are hidden from the queue.
    await refetchSigningTransaction();
    // Open the pending signatures modal
    setPendingSignableIds(signatures.map((signature) => signature.proposalId));
    setPendingDecision(AccountTransactionDecision.INITIATE);
    setIsPendingModalOpen(true);
  };

  const handleSignCreatedTransaction = async (transactionId: string) => {
    setTransactionSubmissionError(null);
    setIsLoading(true);
    // Case: retry signing the saved parent without creating a duplicate transaction.
    try {
      await handleApproveAndSign(transactionId);
    } catch (error) {
      console.error('Error signing created transaction', error);
      setTransactionSubmissionError(submissionError('signing', error));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Validate the shared UI/import cart, create its parent, and request
   * a separate initiator signature for every child.
   */
  const handleSubmit = async () => {
    clearError();
    setTransactionSubmissionError(null);
    // Case: submission requires complete parent/user context and a non-empty cart.
    if (
      !account ||
      network === undefined ||
      !description?.trim() ||
      !user ||
      children.length === 0
    ) {
      setTransactionSubmissionError({
        stage: 'creation',
        message:
          'Missing required account, network, description, or transactions.',
      });
      return;
    }
    let transactionDraft: AccountTransactionDraft;
    // Case: every cart child must still form a valid parent draft before creation.
    try {
      transactionDraft = buildBatchAccountTransactionDraft({
        multichainAccountId: account.id,
        networkId: network,
        description,
        children,
      });
    } catch (error) {
      setTransactionSubmissionError({
        stage: 'creation',
        message:
          error instanceof Error
            ? error.message
            : 'Invalid transaction details.',
      });
      return;
    }
    setIsLoading(true);
    let transactionIdCreatedThisAttempt: string | null = null;

    // Case: retain a successfully created parent ID even if the subsequent signing request fails.
    try {
      const accountTransaction = await createTransaction(transactionDraft);

      transactionIdCreatedThisAttempt = accountTransaction.id;
      setCreatedTransactionId(accountTransaction.id);
      await handleApproveAndSign(accountTransaction.id);
    } catch (error) {
      // Case 1: creation succeeded, so preserve the ID and offer signing again.
      if (transactionIdCreatedThisAttempt !== null) {
        console.error('Error signing created transaction', error);
        setTransactionSubmissionError(submissionError('signing', error));
      } else {
        // Case 2: no transaction exists yet, so the user must submit it again.
        console.error('Error creating transaction', error);
        setTransactionSubmissionError(submissionError('creation', error));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewTransaction = () => {
    reset();
    navigate('/transactions');
  };

  return (
    <>
      <TransactionBuilderHeader
        title="Ready to queue these transactions?"
        showBackButton={!createdTransactionId}
      />
      <div className="mx-auto flex w-full max-w-[932px] flex-col gap-3 px-4 pt-[24px] pb-8">
        <section className="overflow-hidden rounded-[14px] border border-white bg-muted dark:border-zinc-800">
          <h2 className="px-5 py-[7px] text-xs font-normal text-muted-foreground">
            Description
          </h2>
          <div className="rounded-[14px] border border-white bg-neutral-50 p-5 dark:border-zinc-800 dark:bg-zinc-900">
            <p className="whitespace-pre-wrap break-words text-base font-medium">
              {description || 'No description provided'}
            </p>
          </div>
        </section>
        {network !== undefined && (
          <BatchTransactionReview children={children} network={network} />
        )}
      </div>
      <TransactionBuilderFooter
        onContinue={
          createdTransactionId
            ? () => handleSignCreatedTransaction(createdTransactionId)
            : handleSubmit
        }
        continueButtonText={
          createdTransactionId ? 'Sign' : 'Queue transactions'
        }
        continueButtonDisabled={isLoading || children.length === 0}
        secondaryAction={{
          label: 'Simulate transactions',
          icon: <Settings className="size-[15px]" />,
          onClick: () => setSimulationOpen(true),
          disabled: !request || !!createdTransactionId || isLoading,
        }}
      />
      {request && (
        <TransactionSimulationSheet
          key={revision}
          open={simulationOpen}
          onOpenChange={setSimulationOpen}
          source="draft"
          request={request}
        />
      )}
      {/* Pending  Modal - Shown during mobile app signature collection */}
      <PendingSignaturesModal
        open={isPendingModalOpen}
        resetPendingState={resetPendingState}
        refreshSignables={refetchSigningTransaction}
        pendingSignableIds={pendingSignableIds}
        polledSignables={toPendingTransactionSignables(
          signingTransaction ? [signingTransaction] : []
        )}
        pendingDecision={pendingDecision}
        // The top-level by-ID hook owns polling for this parent and its children.
        hookFunctionToPoll={() => undefined}
        onSuccess={setSuccessDialogState}
        isTransaction={true}
      />

      {/* Success Dialog - Shown after transaction is successfully queued */}
      <TransactionSubmitSuccessDialog
        state={successDialogState}
        onViewTransaction={handleViewTransaction}
      />

      {/* Transaction creation/signing error dialog */}
      <Dialog
        open={transactionSubmissionError !== null}
        onOpenChange={(open) => {
          if (!open) setTransactionSubmissionError(null);
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              {transactionSubmissionError?.stage === 'signing'
                ? 'Transaction Signing Failed'
                : 'Transaction Creation Failed'}
            </DialogTitle>
            <DialogDescription className="text-zinc-600">
              {transactionSubmissionError?.message ||
                'This transaction is not valid. Please go back and adjust the transaction details.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTransactionSubmissionError(null)}
              className="w-full sm:w-auto"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
